import AppError from "../utils/AppError.js";
import getEndpoint from "../utils/getEndpoint.js";
import getMessage from "../utils/getMessage.js";
import {
  clearReduxValue,
  getReduxValue,
  setReduxValue,
} from "../utils/redux.js";
import buildQuery from "../utils/queryBuilder.js";
import {
  addBreadcrumb,
  reportApiFailure,
  setLastCapturedRequestId,
} from "../utils/errorReporter.js";

const BASE_URL = import.meta.env.VITE_API_BASE_URL;
const REFRESH_ENDPOINT = getEndpoint("auth", "refresh");
const AUTH_PATH_PREFIX = "/auth/";

let refreshRequestPromise = null;

const isAuthRefreshFailure = (responseData = {}) => {
  const errorName =
    responseData?.errorName ||
    responseData?.error?.name ||
    responseData?.error?.errorName;

  return (
    errorName === "ACCESS_TOKEN_EXPIRED" || errorName === "ACCESS_TOKEN_MISSING"
  );
};

const shouldAttemptRefresh = (endpoint, rawResponse, responseData, retry) => {
  if (!retry) return false;
  if (endpoint.path === REFRESH_ENDPOINT.path) return false;
  if (endpoint.path.startsWith(AUTH_PATH_PREFIX)) return false;

  return isAuthRefreshFailure(responseData) || rawResponse.status === 401;
};

const refreshSession = async () => {
  if (!refreshRequestPromise) {
    refreshRequestPromise = request(REFRESH_ENDPOINT, {}, false).finally(() => {
      refreshRequestPromise = null;
    });
  }

  return refreshRequestPromise;
};

const request = async (endpoint, options = {}, retry = true) => {
  if (!endpoint?.method || !endpoint?.path) {
    throw new AppError(
      500,
      getMessage("common", "invalidInput", "eng", "endpoint"),
    );
  }

  const { body = {}, headers = {}, query, signal } = options;

  const url = BASE_URL + endpoint.path + buildQuery(query);

  const accessToken = getReduxValue("auth.accessToken");

  const fetchOptions = {
    method: endpoint.method,
    headers: {
      ...(body instanceof FormData
        ? {}
        : { "Content-Type": "application/json" }),
      ...(accessToken && endpoint.path !== REFRESH_ENDPOINT.path
        ? { Authorization: `Bearer ${accessToken}` }
        : {}),
      ...headers,
    },
    credentials: endpoint.credentials || "omit",
    signal,
  };

  if (endpoint.method.toUpperCase() !== "GET") {
    fetchOptions.body = body instanceof FormData ? body : JSON.stringify(body);
  }

  let rawResponse;
  try {
    rawResponse = await fetch(url, fetchOptions);
  } catch (error) {
    if (error?.name === "AbortError") {
      throw error;
    }
    reportApiFailure({
      statusCode: 0,
      message: getMessage("common", "networkError"),
      endpointPath: endpoint.path,
      method: endpoint.method,
      query,
    });
    throw new AppError(0, getMessage("common", "networkError"));
  }

  const responseRequestId = rawResponse.headers.get("x-request-id");
  setLastCapturedRequestId(responseRequestId);

  let responseData = null;
  try {
    responseData = await rawResponse.json();
  } catch (error) {
    reportApiFailure({
      statusCode: rawResponse.status,
      message: getMessage("common", "invalidJSON"),
      endpointPath: endpoint.path,
      method: endpoint.method,
      requestId: responseRequestId,
      query,
    });
    throw new AppError(rawResponse.status, getMessage("common", "invalidJSON"));
  }

  if (shouldAttemptRefresh(endpoint, rawResponse, responseData, retry)) {
    try {
      await refreshSession();
      return await request(endpoint, options, false);
    } catch (error) {
      clearReduxValue("auth.accessToken");
      clearReduxValue("user.user");
      throw new AppError(401, "Session expired. Please login again.");
    }
  }
  if (!rawResponse.ok) {
    reportApiFailure({
      statusCode: rawResponse.status,
      message: responseData?.message || getMessage("common", "requestFailed"),
      endpointPath: endpoint.path,
      method: endpoint.method,
      requestId: responseRequestId,
      responseBody: responseData,
      query,
    });

    if (rawResponse.status >= 500) {
      addBreadcrumb("api_5xx", {
        endpoint: endpoint.path,
        method: endpoint.method,
        requestId: responseRequestId,
        statusCode: rawResponse.status,
      });
    }

    if (rawResponse.status === 401) {
      clearReduxValue("auth.accessToken");
      clearReduxValue("user.user");
      if (getReduxValue("auth.initialized")) window.location.replace("/login");
    }
    throw new AppError(
      rawResponse.status,
      responseData?.message || getMessage("common", "requestFailed"),
      undefined,
      true,
      responseData?.error,
    );
  }

  // Handle automatic accessToken storing
  if (endpoint.acceptAccessToken && responseData?.data?.accessToken) {
    setReduxValue("auth.accessToken", responseData.data.accessToken);
    const user = await request(getEndpoint("user", "getMe"), {}, false);
    setReduxValue("user.user", user.data);
  }

  // Handle automatic accessToken clearing
  if (endpoint.clearAccessToken) {
    clearReduxValue("auth.accessToken");
    clearReduxValue("user.user");
  }

  return responseData;
};

export default request;
